/**
 * Following a job the site handed to the person's own machine.
 *
 * Image, video and audio all wait the same way: poll the job, read its
 * latest events, say what the machine is doing. Kept in one place so a
 * lesson learned on one kind -- the model-download sentences, the wait that
 * must outlast a first download -- is not lost on the others.
 */
import { APIClient } from "../api/client.js";
import type { GenerationJob, JobEvent } from "../types/index.js";

export type HostedJobResult = {
	artifacts?: Array<{ url?: string | null; media_type?: string | null }>;
	error?: string | null;
	metrics?: Record<string, number>;
	data?: Record<string, unknown>;
};

/** What the page should show while it waits. */
export type HostedProgress =
	| { preparing: true; message: string; percent: number }
	| { preparing: false; percent: number };

// The server decides when a job has failed: it now ends one only after its
// machine goes silent, however long a first model download takes. Thirty
// minutes of polling here reported a failure while the job was still
// progressing, so the page waits as long as a working machine could.
export const HOSTED_JOB_MAX_WAIT_MS = 6 * 60 * 60 * 1000;
export const HOSTED_JOB_INITIAL_POLL_MS = 2500;
export const HOSTED_JOB_MAX_POLL_MS = 5000;

function hostedPollDelay(elapsedMs: number): number {
	// First-model startup benefits from a little responsiveness; after 30 s,
	// 5 s is plenty and avoids hammering the API during multi-hour video jobs.
	return elapsedMs < 30_000 ? HOSTED_JOB_INITIAL_POLL_MS : HOSTED_JOB_MAX_POLL_MS;
}

/**
 * Read a job's events the way the page shows them.
 *
 * While the machine prepares the model -- on a first run, mostly a
 * multi-gigabyte download -- its sentence wins over a percentage that would
 * sit still for minutes and read as a frozen page. `null` means nothing to
 * report yet.
 */
export function readHostedProgress(
	allEvents: JobEvent[],
	status: GenerationJob["status"]
): HostedProgress | null {
	// "Still working" events only keep the server's clocks going. A server
	// that predates them stores them; they must not hide what came before.
	const events = allEvents.filter((event) => event.metadata?.stage !== "alive");
	const latest = events[events.length - 1];
	if (latest?.metadata?.stage === "loading" && latest.message) {
		return { preparing: true, message: latest.message, percent: Number(latest.metadata.percent) };
	}
	for (let index = events.length - 1; index >= 0; index -= 1) {
		const progress = events[index].progress;
		if (typeof progress === "number") return { preparing: false, percent: progress };
	}
	return status === "running" ? { preparing: false, percent: 10 } : null;
}

/** A job that ended without success, still knowing which job it was. */
export class HostedJobFailure extends Error {
	constructor(
		message: string,
		readonly jobId: string
	) {
		super(message);
	}
}

/**
 * Poll a job until it ends, reporting progress on the way.
 *
 * Resolves with the job's result when it succeeds; rejects with the
 * worker's own error, or `failureMessage` when it gave none.
 */
/** How long the wait rides out a site it cannot reach before giving up.
 * A redeploy takes the site away for about a minute. */
const UNREACHABLE_GRACE_MS = 3 * 60 * 1000;

/** A failure worth waiting through: no answer at all, or the site itself
 * failing. A refusal (session gone, job unknown) is an answer, and final. */
function isPassing(error: unknown): boolean {
	const status = (error as { status?: number }).status;
	return status === undefined || status === 0 || status >= 500;
}

export async function waitForHostedResult(
	job: GenerationJob,
	onProgress: (progress: HostedProgress) => void,
	failureMessage: string
): Promise<HostedJobResult> {
	const startedAt = Date.now();
	let eventCursor = 0;
	let recentEvents: JobEvent[] = [];
	let unreachableSince: number | null = null;

	while (Date.now() - startedAt < HOSTED_JOB_MAX_WAIT_MS) {
		let current: GenerationJob;
		try {
			current = await APIClient.getJob(job.job_id);
			unreachableSince = null;
		} catch (error) {
			// The machine keeps drawing while the page cannot ask how it is
			// going: one lost answer -- a phone between two cells, the site
			// being redeployed -- used to end the wait and drop the image.
			if (!isPassing(error)) throw error;
			unreachableSince ??= Date.now();
			if (Date.now() - unreachableSince > UNREACHABLE_GRACE_MS) throw error;
			await new Promise<void>((resolve) => window.setTimeout(resolve, 5000));
			continue;
		}
		try {
			const page = await APIClient.getJobEventPage(job.job_id, eventCursor);
			eventCursor = page.next_cursor;
			if (page.events.length) {
				recentEvents = [...recentEvents, ...page.events].slice(-100);
			}
			const progress = readHostedProgress(recentEvents, current.status);
			if (progress) onProgress(progress);
		} catch {
			// Job status remains authoritative when event polling is unavailable.
		}
		if (current.status === "succeeded") return (current.result || {}) as HostedJobResult;
		if (current.status === "failed" || current.status === "cancelled") {
			const result = (current.result || {}) as HostedJobResult;
			throw new HostedJobFailure(result.error || failureMessage, job.job_id);
		}
		const elapsed = Date.now() - startedAt;
		await new Promise<void>((resolve) => window.setTimeout(resolve, hostedPollDelay(elapsed)));
	}
	throw new Error("La génération n'a pas abouti après plusieurs heures.");
}

/** The first artifact URL of a finished job, if any. */
export function firstArtifactUrl(result: HostedJobResult): string | undefined {
	return (result.artifacts || [])
		.map((artifact) => artifact.url)
		.find((url): url is string => Boolean(url));
}

/** Where the site sends a job: the machine the person picked, else their own. */
export function routeToOwnMachine(workerId: string | null | undefined) {
	return workerId
		? { target_worker_id: workerId }
		: { prefer_local: true, allow_shared_workers: true };
}
